import { inject } from 'vue'
import { useHelpHub } from '@/hooks/useHelpHub'
import { safeCapture } from '@/helpers/posthog'
import { POSTHOG_EVENTS, CDP_SUPPORT_URL, CDP_SUPPORT_VIDEOS_URL, HELP_HUB_GET_STARTED_SLUG, LINK_TARGET } from '@/constants'

// The "Explore help topics" rows, static until the Help content API serves them. Each opens a hub page, an outside page, or the feedback dialog.
// Not in constants/helpHub.js: constants/index.js re-exports that file and holds the support URLs, so importing them there would be a cycle.
export const HELP_HUB_TOPICS = [
  { key: 'getting-started', title: 'Getting started', description: 'Learn the basics of card scanning & importing', help: HELP_HUB_GET_STARTED_SLUG },
  { key: 'guides', title: 'Help guides & documentation', description: 'In-depth articles for every feature', href: CDP_SUPPORT_URL },
  { key: 'videos', title: 'Video tutorials', description: 'Watch step-by-step walkthroughs', href: CDP_SUPPORT_VIDEOS_URL },
  { key: 'feedback', title: 'Submit feedback', description: 'Report a bug or request a feature', feedback: true },
]

// The focus ring the card draws for its stretched link, on a card that is the control itself.
const FOCUS_CLASS = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300'

// Which of the three link kinds a row is, in the shared vocabulary.
const targetTypeOf = (topic) => {
  if (topic.help) return LINK_TARGET.HELP_PAGE
  if (topic.href) return LINK_TARGET.EXTERNAL_URL
  return LINK_TARGET.CDP_ROUTE
}

// What makes a topic row a HelpHubLinkCard control, on the Explore section and among the search results: a stretched link to a hub page, or the card itself as an outside link or a button.
export function useHelpTopics () {
  const { helpLink } = useHelpHub()
  // MainLayout's Canny board, the app's one feedback entry point.
  const feedbackDialog = inject('showFeedbackDialog')

  // A Help Categories row has no key: it isn't a topic the event knows.
  const trackTopic = (topic) => topic.key && safeCapture(POSTHOG_EVENTS.HELP_TOPIC_CLICKED, {
    topic_id: topic.key,
    target_type: targetTypeOf(topic),
  })

  const control = (topic) => {
    const onClick = () => trackTopic(topic)

    if (topic.help) return { as: 'div', to: helpLink(topic.help), onClick }
    if (topic.href) return { as: 'a', href: topic.href, target: '_blank', rel: 'noopener noreferrer', class: FOCUS_CLASS, onClick }
    return {
      as: 'button',
      type: 'button',
      class: `w-full text-left ${FOCUS_CLASS}`,
      onClick: () => { onClick(); feedbackDialog.value.open = true },
    }
  }

  return { control }
}
